import React from 'react';

import Head from 'next/head';
import { MessageDescriptor, useIntl } from 'react-intl';

import { localizeLink } from '~/i18n';
import { baseUrl } from '~/links/messages';
import { prefixWithBasePath } from '~/utils/links';

const PageHead = ({
  title,
  description,
  href,
  ogcover,
  children,
}: {
  title: string;
  description?: MessageDescriptor | string;
  href: string;
  ogcover?: string;
  children?: React.ReactNode;
}): JSX.Element => {
  const defaultLocale = 'en';
  const otherLocales = ['ja'];
  const intl = useIntl();
  // If a description is provided, use that, otherwise messagedescriptor can be provided too
  if (typeof description === 'object') {
    description = intl.formatMessage(description);
  }
  return (
    <Head>
      <title>{title}</title>
      {description ? (
        <meta
          data-testid="metaDescription"
          lang={intl.locale}
          name="description"
          content={description}
        />
      ) : null}
      <meta
        property="og:image"
        content={ogcover || '/developer/ogcover.png'}
        key="cover-image"
      />
      <link
        rel="canonical"
        data-testid="cLink"
        href={
          baseUrl +
          prefixWithBasePath(
            localizeLink(intl.locale, intl.defaultLocale, href),
          )
        }
      />
      {otherLocales.map((locale) => {
        return (
          <link
            data-testid="aLink"
            key={locale}
            hrefLang={locale}
            rel="alternate"
            href={
              baseUrl +
              prefixWithBasePath(localizeLink(locale, defaultLocale, href))
            }
          />
        );
      })}
      <link
        data-testid="aDefault"
        hrefLang={defaultLocale}
        rel="alternate"
        href={baseUrl + prefixWithBasePath(href)}
      />
      <link
        data-testid="xDefault"
        hrefLang="x-default"
        rel="alternate"
        href={baseUrl + prefixWithBasePath(href)}
      />
      {/* OG tags */}
      <meta property="og:title" content={title} key="og-title" />
      <meta
        property="og:image"
        content={`${baseUrl}${ogcover || '/developer/ogcover.png'}`}
        key="cover-image"
      />
      <meta
        property="og:url"
        content={
          baseUrl +
          prefixWithBasePath(
            localizeLink(intl.locale, intl.defaultLocale, href),
          )
        }
        key="og-url"
      />
      <meta
        property="og:description"
        content={description}
        key="og-description"
      />
      {children}
    </Head>
  );
};

export default PageHead;
